import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Sms as SmsIcon,
  AccountBalance as AccountBalanceIcon
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import {
  getSystemSettings,
  createSystemSetting,
  updateSystemSetting,
  deleteSystemSetting,
  initializeDefaultSettings
} from "../services/settingsService";
import { SystemSetting, SystemSettingCreate, SystemSettingUpdate } from "../types";
import api from '../api/api';
import { MpesaSettingsPanel } from '../components/MpesaSettingsPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SystemSettings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [showSensitiveValues, setShowSensitiveValues] = useState(false);
  const [whatsappTestLoading, setWhatsappTestLoading] = useState(false);
  const [whatsappTestResult, setWhatsappTestResult] = useState<any>(null);
  const [testMessageDialog, setTestMessageDialog] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [testMessage, setTestMessage] = useState("This is a test message from A3S Labs WhatsApp integration.");
  const [sendingTestMessage, setSendingTestMessage] = useState(false);
  const [smsTestLoading, setSmsTestLoading] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState<any>(null);
  const [testSmsDialog, setTestSmsDialog] = useState(false);
  const [testSmsPhoneNumber, setTestSmsPhoneNumber] = useState("");
  const [testSmsMessage, setTestSmsMessage] = useState("This is a test message from CVision SMS integration.");
  const [sendingTestSms, setSendingTestSms] = useState(false);

  // Form state
  const [formData, setFormData] = useState<SystemSettingCreate>({
    key: "",
    value: "",
    description: "",
    category: "general",
    is_sensitive: false
  });

  // Check if user is superuser
  const isSuperUser = user?.role === 'superuser';

  useEffect(() => {
    if (isSuperUser) {
      fetchSettings();
    }
  }, [isSuperUser]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/');
      setSettings(response.data as any[]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (setting?: SystemSetting) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        key: setting.key,
        value: setting.value || "",
        description: setting.description || "",
        category: setting.category,
        is_sensitive: setting.is_sensitive
      });
    } else {
      setEditingSetting(null);
      setFormData({
        key: "",
        value: "",
        description: "",
        category: "general",
        is_sensitive: false
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSetting(null);
    setFormData({
      key: "",
      value: "",
      description: "",
      category: "general",
      is_sensitive: false
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingSetting) {
        await api.put(`/settings/${editingSetting.key}`, {
          value: formData.value,
          description: formData.description,
          category: formData.category,
          is_sensitive: formData.is_sensitive
        });
        setSuccess("Setting updated successfully");
      } else {
        await api.post('/settings/', formData);
        setSuccess("Setting created successfully");
      }
      handleCloseDialog();
      fetchSettings();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save setting");
    }
  };

  const handleDelete = async (key: string) => {
    if (window.confirm("Are you sure you want to delete this setting?")) {
      try {
        await api.delete(`/settings/${key}`);
        setSuccess("Setting deleted successfully");
        fetchSettings();
      } catch (err: any) {
        setError(err?.response?.data?.detail || "Failed to delete setting");
      }
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      await api.post('/settings/initialize-defaults');
      setSuccess("Default settings initialized successfully");
      fetchSettings();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to initialize default settings");
    }
  };

  const getFilteredSettings = (category: string) => {
    return settings.filter(setting => setting.category === category);
  };

  const getDisplayValue = (setting: SystemSetting) => {
    if (setting.is_sensitive && !showSensitiveValues) {
      return "••••••••";
    }
    return setting.value || "";
  };

  const handleWhatsappTest = async () => {
    setWhatsappTestLoading(true);
    setWhatsappTestResult(null);
    try {
      const response = await api.post('/settings/test-whatsapp');
      setWhatsappTestResult(response.data);
      if ((response.data as any).whatsapp_test.success) {
        setSuccess("WhatsApp connection test successful!");
      } else {
        setError("WhatsApp connection test failed: " + (response.data as any).whatsapp_test.error);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to test WhatsApp connection");
    } finally {
      setWhatsappTestLoading(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testPhoneNumber.trim()) {
      setError("Please enter a phone number");
      return;
    }
    
    setSendingTestMessage(true);
    try {
      const response = await api.post('/settings/send-test-whatsapp', {
        phone_number: testPhoneNumber,
        message: testMessage
      });
      
      if ((response.data as any).test_message_sent.success) {
        setSuccess("Test message sent successfully!");
        setTestMessageDialog(false);
      } else {
        setError("Failed to send test message: " + (response.data as any).test_message_sent.error);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to send test message");
    } finally {
      setSendingTestMessage(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!testSmsPhoneNumber.trim()) {
      setError("Please enter a phone number");
      return;
    }
    
    setSendingTestSms(true);
    try {
      const response = await api.post('/settings/test-sms', {
        phone_number: testSmsPhoneNumber,
        message: testSmsMessage
      });
      
      if ((response.data as any).sms_test.success) {
        setSuccess("SMS test message sent successfully!");
        setTestSmsDialog(false);
      } else {
        setError("Failed to send SMS test message: " + (response.data as any).sms_test.error);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to send SMS test message");
    } finally {
      setSendingTestSms(false);
    }
  };

  if (!isSuperUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access system settings.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          System Settings
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={handleInitializeDefaults}
            sx={{ mr: 1 }}
            disabled={loading}
          >
            Initialize Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Setting
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={showSensitiveValues}
              onChange={(e) => setShowSensitiveValues(e.target.checked)}
            />
          }
          label="Show Sensitive Values"
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
            <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="Email" icon={<EmailIcon />} iconPosition="start" />
            <Tab label="WhatsApp" icon={<WhatsAppIcon />} iconPosition="start" />
            <Tab label="SMS" icon={<SmsIcon />} iconPosition="start" />
            <Tab label="M-Pesa" icon={<AccountBalanceIcon />} iconPosition="start" />
            <Tab label="Company" icon={<BusinessIcon />} iconPosition="start" />
            <Tab label="Billing" icon={<PaymentIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <SettingsTable
            settings={getFilteredSettings("general")}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            showSensitiveValues={showSensitiveValues}
            getDisplayValue={getDisplayValue}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SettingsTable
            settings={getFilteredSettings("email")}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            showSensitiveValues={showSensitiveValues}
            getDisplayValue={getDisplayValue}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>WhatsApp Configuration</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={whatsappTestLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                onClick={handleWhatsappTest}
                disabled={whatsappTestLoading}
              >
                Test Connection
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setTestMessageDialog(true)}
              >
                Send Test Message
              </Button>
            </Box>
            {whatsappTestResult && (
              <Alert 
                severity={whatsappTestResult.whatsapp_test.success ? "success" : "error"}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>Connection Status:</strong> {whatsappTestResult.settings_configured ? "Configured" : "Not Configured"}
                </Typography>
                {whatsappTestResult.whatsapp_test.success && (
                  <Typography variant="body2">
                    <strong>Account:</strong> {whatsappTestResult.whatsapp_test.account_name}
                  </Typography>
                )}
                {!whatsappTestResult.whatsapp_test.success && (
                  <Typography variant="body2">
                    <strong>Error:</strong> {whatsappTestResult.whatsapp_test.error}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
          <SettingsTable
            settings={getFilteredSettings("whatsapp")}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            showSensitiveValues={showSensitiveValues}
            getDisplayValue={getDisplayValue}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>SMS Configuration</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setTestSmsDialog(true)}
              >
                Send Test Message
              </Button>
            </Box>
            {/* Add SMS test result display here if implemented */}
          </Box>
          <SettingsTable
            settings={getFilteredSettings("sms")}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            showSensitiveValues={showSensitiveValues}
            getDisplayValue={getDisplayValue}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <MpesaSettingsPanel />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <SettingsTable
            settings={getFilteredSettings("company")}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            showSensitiveValues={showSensitiveValues}
            getDisplayValue={getDisplayValue}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <SettingsTable
            settings={getFilteredSettings("billing")}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            showSensitiveValues={showSensitiveValues}
            getDisplayValue={getDisplayValue}
          />
        </TabPanel>
      </Paper>

      {/* Settings Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSetting ? "Edit Setting" : "Add New Setting"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Key"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              disabled={!!editingSetting}
              fullWidth
              required
            />
            <TextField
              label="Value"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
                <MenuItem value="company">Company</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_sensitive}
                  onChange={(e) => setFormData({ ...formData, is_sensitive: e.target.checked })}
                />
              }
              label="Sensitive (password, API key, etc.)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingSetting ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Message Dialog */}
      <Dialog open={testMessageDialog} onClose={() => setTestMessageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test WhatsApp Message</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Phone Number"
              value={testPhoneNumber}
              onChange={(e) => setTestPhoneNumber(e.target.value)}
              placeholder="+254700000000"
              fullWidth
              required
              helperText="Enter phone number with country code (e.g., +254700000000)"
            />
            <TextField
              label="Message"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestMessageDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSendTestMessage} 
            variant="contained"
            disabled={sendingTestMessage || !testPhoneNumber.trim()}
            startIcon={sendingTestMessage ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {sendingTestMessage ? "Sending..." : "Send Test Message"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS Test Message Dialog */}
      <Dialog open={testSmsDialog} onClose={() => setTestSmsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test SMS Message</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Phone Number"
              value={testSmsPhoneNumber}
              onChange={(e) => setTestSmsPhoneNumber(e.target.value)}
              placeholder="+254700000000"
              fullWidth
              required
              helperText="Enter phone number with country code (e.g., +254700000000)"
            />
            <TextField
              label="Message"
              value={testSmsMessage}
              onChange={(e) => setTestSmsMessage(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestSmsDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSendTestSms} 
            variant="contained"
            disabled={sendingTestSms || !testSmsPhoneNumber.trim()}
            startIcon={sendingTestSms ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {sendingTestSms ? "Sending..." : "Send Test SMS"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface SettingsTableProps {
  settings: SystemSetting[];
  onEdit: (setting: SystemSetting) => void;
  onDelete: (key: string) => void;
  showSensitiveValues: boolean;
  getDisplayValue: (setting: SystemSetting) => string;
}

const SettingsTable: React.FC<SettingsTableProps> = ({
  settings,
  onEdit,
  onDelete,
  showSensitiveValues,
  getDisplayValue
}) => {
  if (settings.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          No settings found in this category.
        </Typography>
      </Box>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Key</TableCell>
          <TableCell>Value</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Category</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {settings.map((setting) => (
          <TableRow key={setting.key} hover>
            <TableCell>
              <Typography variant="body2" fontWeight="medium">
                {setting.key}
              </Typography>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {getDisplayValue(setting)}
                </Typography>
                {setting.is_sensitive && (
                  <IconButton size="small">
                    {showSensitiveValues ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )}
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="body2" color="textSecondary">
                {setting.description || "-"}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip label={setting.category} size="small" color="primary" />
            </TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => onEdit(setting)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => onDelete(setting.key)} color="error">
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SystemSettings; 